<script setup lang="ts">
import SectionFederationEmployeeRelationRepository from '@legacy/js/repository/SectionFederationEmployeeRelationRepository';
import EventBus from '@legacy/js/service/EventBus';
import axios from 'axios';
import { Trash2 } from 'lucide';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { SphButton } from '@/components/base/button';
import { SphSelect } from '@/components/base/select';
import { useI18n } from '@/composables/i18n';
import { useNotifications } from '@/composables/notifications';

defineOptions({
    compatConfig: { MODE: 3 },
});

const props = defineProps<{
    federationId: number
    federationEmployeeId: number
}>();

const { i18n } = useI18n();
const { notify } = useNotifications();

interface Section { id: number, name: string }
interface Relation { id: number, section: Section }

const relations = ref<Relation[]>([]);
const sections = ref<Section[]>([]);
const selectedSectionId = ref<string | undefined>(undefined);
const pending = ref(false);

const sectionOptions = computed(() =>
    sections.value
        .filter((s) => !relations.value.some((r) => r.section.id === s.id))
        .map((s) => ({ value: String(s.id), label: s.name })),
);

function loadSections() {
    axios.get(`/federations/${props.federationId}/sections`)
        .then((r) => {
            sections.value = r.data.data.map((d: any) => ({ id: d.id, name: d.name }));
        })
        .catch(() => notify.error('error.loadingData'));
}

const loadingRelations = ref(false);
function loadRelations() {
    if (!props.federationEmployeeId) {
        relations.value = [];
        return;
    }

    loadingRelations.value = true;

    axios.get(`/federation-employees/${props.federationEmployeeId}/section-relations`)
        .then((r) => {
            relations.value = r.data.data.map((d: any) => ({
                id: d.id,
                section: { id: d.section.data.id, name: d.section.data.name },
            }));
        })
        .catch(() => notify.error('error.loadingData'))
        .finally(() => {
            loadingRelations.value = false;
        });
}

let unsubscribeSection: (() => void) | undefined;

onMounted(() => {
    loadSections();
    loadRelations();
    unsubscribeSection = EventBus.$on('section:changed', (federationId: number) => {
        if (federationId === props.federationId) {
            loadSections();
            loadRelations();
        }
    });
});

onUnmounted(() => {
    unsubscribeSection?.();
});

defineExpose({ sections, relations, selectedSectionId, sectionOptions });

watch(() => props.federationEmployeeId, () => {
    selectedSectionId.value = undefined;
    loadRelations();
});

function create() {
    if (!selectedSectionId.value) {
        return;
    }
    pending.value = true;
    SectionFederationEmployeeRelationRepository.create(props.federationEmployeeId, Number(selectedSectionId.value))
        .then(() => {
            selectedSectionId.value = undefined;
            notify.success('saveChanges.success');
            loadRelations();
        })
        .catch(() => notify.error('saveChanges.error'))
        .finally(() => {
            pending.value = false;
        });
}

function remove(relation: Relation) {
    ;(window as any).showConfirm(i18n.t('confirm'), i18n.t('section.relation.confirm.delete', { name: relation.section.name }), () => {
        SectionFederationEmployeeRelationRepository.remove(relation.id)
            .then(() => {
                relations.value = relations.value.filter((r) => r.id !== relation.id);
                notify.success('section.relation.delete.success', { name: relation.section.name });
            })
            .catch(() => notify.error('saveChanges.error'));
    }, 'Ok');
}
</script>

<template>
    <table class="kgr-table kgr-table--striped kgr-table--block table-groups kgr-row text-foreground">
        <thead>
            <tr class="kgr-table__row">
                <th class="kgr-table__cell kgr-table__cell--headline w-full">
                    {{ i18n.t('section') }}
                </th>
                <th class="kgr-table__cell kgr-table__cell--headline"></th>
            </tr>
        </thead>
        <tbody v-if="federationEmployeeId">
            <tr class="kgr-table__row">
                <td class="kgr-table__cell">
                    <SphSelect
                        v-model="selectedSectionId"
                        :options="sectionOptions"
                        :placeholder="i18n.t('section')"
                    />
                </td>
                <td class="kgr-table__cell"></td>
            </tr>
            <tr
                v-for="relation in relations"
                :key="relation.id"
                class="kgr-table__row"
            >
                <td class="kgr-table__cell">
                    {{ relation.section.name }}
                </td>
                <td class="kgr-table__cell kgr-table__cell--actions">
                    <SphButton
                        :icon="Trash2"
                        variant="transparent"
                        size="md"
                        class="kgr-table__action"
                        @click="remove(relation)"
                    />
                </td>
            </tr>
        </tbody>
    </table>
    <div
        v-if="federationEmployeeId"
        class="kgr-text kgr-text--rightAligned"
    >
        <SphButton
            :label="i18n.t('saveChanges')"
            :disabled="!selectedSectionId"
            :loading="pending"
            @click="create"
        />
    </div>
</template>
